import React, { FC, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import query from "query-string";
import firebase from "firebase/app";
import moment from "moment";
import { StyledPaymentMethod } from "..";
import PaymentModal from "../../User/PaymentModal";
import { FlexGroup } from "../../Grid";
import { Spinner } from "../../System";

import { SubInfoType } from "../../../types";

import MasterCard from "../../icons/mastercard-logo.svg";
import Visa from "../../icons/visa.svg";
import Discover from "../../icons/discover.svg";
import AmericanExpress from "../../icons/american-express.svg";
import JCB from "../../icons/jcb.svg";

type Props = {
  subscription?: SubInfoType | null;
};

export type PaymentInfoType = {
  cardName: string;
  last4: string;
  expMonth: string;
  expYear: string;
};

const PaymentMethod: FC<Props> = ({ subscription }) => {
  const location = useLocation();
  const urlQuery = query.parse(location.search);
  const openModal = !!urlQuery.showModal;
  const getPaymentInfo = firebase.functions().httpsCallable("getPaymentInfo");

  const [showModal, setShowModal] = useState(openModal);
  const [errorText, setErrorText] = useState("");
  const [cardInfo, setCardInfo] = useState<
    PaymentInfoType | undefined | null
  >();

  useEffect(() => {
    if (showModal) {
      return;
    }
    getPaymentInfo({})
      .then((res) => {
        if (!res.data?.card) {
          setCardInfo(null);
          return;
        }
        setCardInfo({
          cardName: res.data.card.brand,
          last4: res.data.card.last4,
          expMonth: res.data.card.exp_month.toString(),
          expYear: res.data.card.exp_year.toString().slice(2),
        });
      })
      .catch((e) => setErrorText(e.message));
  }, [showModal]);

  const returnCardIcon = (cardName: string) => {
    switch (cardName) {
      case "visa":
        return Visa;
      case "mastercard":
        return MasterCard;
      case "amex":
        return AmericanExpress;
      case "jcb":
        return JCB;
      case "discover":
        return Discover;
      default:
        return Visa;
    }
  };

  return (
    <StyledPaymentMethod>
      <div className="billing-header">
        <h3 className="billing-subheader">Payment Method</h3>
        <button className="billing-button" onClick={() => setShowModal(true)}>
          Update
        </button>
      </div>

      {!cardInfo && cardInfo !== null && <Spinner />}

      {cardInfo === null && (
        <div className="payment-method-empty-state">
          <span>No card information was found.</span>
        </div>
      )}

      {cardInfo && (
        <div className="payment-method-container">
          {errorText && <p>{errorText}</p>}

          {cardInfo !== null && (
            <>
              <div className="payment-method-card">
                <FlexGroup>
                  <div className="card-logo">
                    <img
                      src={returnCardIcon(cardInfo.cardName)}
                      alt="credit card logo"
                    />
                  </div>
                  <p className="card-name">
                    {cardInfo?.cardName} **** {cardInfo?.last4}
                  </p>
                </FlexGroup>
              </div>
            </>
          )}
          {subscription && (
            <div className="subscription-payment-info">
              {subscription.start && (
                <p>
                  Your billing cycle started on{" "}
                  {moment.unix(subscription.start).format("M/D/YY")}
                </p>
              )}
              {subscription.end && subscription.renews && (
                <p>
                  Your subscription{" "}
                  <span className="subscription-renew">
                    renews on {moment.unix(subscription.end).format("M/D/YY")}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <PaymentModal show={showModal} setShow={setShowModal} />
    </StyledPaymentMethod>
  );
};

export default PaymentMethod;
